import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react"

import { useTableFocus } from "@/human-feedback/focus"

interface NumericalAnnotationInputHandle {
  focusInput: (initialKey?: string) => void
}

interface NumericalAnnotationInputProps {
  currentValue: number | null
  onValueChange: (
    floatValue: number | null,
    stringValue: string | null,
    setValue: string[] | null,
  ) => void
  metricId: string
  minValue: number
  maxValue: number
  readOnly?: boolean
}

export const NumericalAnnotationInput = forwardRef<
  NumericalAnnotationInputHandle,
  NumericalAnnotationInputProps
>(
  (
    { currentValue, onValueChange, metricId, minValue, maxValue, readOnly },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [inputValue, setInputValue] = useState(
      currentValue != null ? String(currentValue) : "",
    )

    // Sync local state when the saved annotation changes
    useEffect(() => {
      setInputValue(currentValue != null ? String(currentValue) : "")
    }, [currentValue])

    const saveValue = useCallback(() => {
      if (readOnly) {
        return
      }
      const trimmed = inputValue.trim()
      if (trimmed === "") {
        // Clear the value
        if (currentValue != null) {
          onValueChange(null, null, null)
        }
        return
      }
      const numValue = Number(trimmed)
      if (!isNaN(numValue) && numValue >= minValue && numValue <= maxValue) {
        onValueChange(numValue, null, null)
      } else {
        // Invalid — revert to saved value
        setInputValue(currentValue != null ? String(currentValue) : "")
      }
    }, [inputValue, currentValue, minValue, maxValue, onValueChange, readOnly])

    const handleBlur = useCallback(() => {
      saveValue()
    }, [saveValue])

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          e.preventDefault()
          e.currentTarget.blur()
          // Move focus to the next metric row
          const currentRow = e.currentTarget.closest("tr.metric-row")
          const nextRow = currentRow?.nextElementSibling as HTMLElement | null
          if (nextRow?.matches("tr.metric-row")) {
            nextRow.focus()
          }
          return
        }
        // Prevent row-level keyboard shortcuts from firing while typing
        e.stopPropagation()
      },
      [],
    )

    // Expose focus method for keyboard shortcut activation
    useImperativeHandle(
      ref,
      () => ({
        focusInput: (initialKey?: string) => {
          if (readOnly) {
            return
          }
          if (initialKey != null) {
            setInputValue(initialKey)
          }
          requestAnimationFrame(() => {
            inputRef.current?.focus()
          })
        },
      }),
      [readOnly],
    )

    // Register numerical focus callback with the table context
    const { registerNumericalFocusCallback, unregisterNumericalFocusCallback } =
      useTableFocus()

    useEffect(() => {
      if (readOnly) {
        return
      }
      const focusFn = (initialKey?: string) => {
        if (initialKey != null) {
          setInputValue(initialKey)
        }
        requestAnimationFrame(() => {
          inputRef.current?.focus()
        })
      }
      registerNumericalFocusCallback(metricId, focusFn)
      return () => unregisterNumericalFocusCallback(metricId)
    }, [
      metricId,
      readOnly,
      registerNumericalFocusCallback,
      unregisterNumericalFocusCallback,
    ])

    return (
      <div className="flex flex-col items-center gap-0.5">
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          aria-label={`Human review score for metric ${metricId}`}
          value={inputValue}
          onChange={readOnly ? undefined : (e) => setInputValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Input review score"
          readOnly={readOnly}
          className="rounded-sm min-h-[24px] w-32 px-2 py-0.5 text-xs inline-flex items-center justify-center bg-surface-1 text-coval-text text-center outline-none"
        />
        <p className="text-[10px] text-coval-text-muted">
          {minValue}–{maxValue}
        </p>
      </div>
    )
  },
)

NumericalAnnotationInput.displayName = "NumericalAnnotationInput"
